import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Box, Link, Typography } from '@mui/material'
import { ASSISTANT_LINK_ALIASES, resolveAssistantPath } from './portalPaths'
import type { AssistantRouteTable } from './assistantRoutes'

interface AssistantMarkdownProps {
  content: string
  /** In-app navigation for portal paths (keeps SPA routing). */
  onNavigate?: (path: string) => void
  /** Live menu routes, so shortcuts match the pages the router actually has. */
  routes?: AssistantRouteTable | null
  /** Tighter type scale for the phone drawer. */
  dense?: boolean
}

const PORTAL_PATH_RE = /(?:^|[\s(])(\/(?:admin|optic|client|bank)[^\s)\]}'"]*)/g

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function linkifyMatch(
  content: string,
  full: string,
  path: string,
  routes?: AssistantRouteTable | null,
): string {
  const cleaned = path.replace(/[`'".;,!?]+$/, '')
  const resolved = resolveAssistantPath(cleaned, routes)
  if (!resolved) return full
  const prefix = full.slice(0, full.length - path.length)
  if (content.includes(`](${cleaned})`) || content.includes(`](${resolved})`)) {
    return full
  }
  return `${prefix}[${cleaned}](${resolved})`
}

function shortcutPattern(routes?: AssistantRouteTable | null): RegExp {
  const keys = Object.keys({ ...ALIASES_FOR_LINKIFY, ...routes?.aliases }).sort(
    (left, right) => right.length - left.length,
  )
  return new RegExp(
    `(?:^|[\\s(])(${keys.map(escapeRegExp).join('|')})(?=$|[\\s).,;:!?'"\`])`,
    'g',
  )
}

const ALIASES_FOR_LINKIFY = Object.fromEntries(ASSISTANT_LINK_ALIASES.map(alias => [alias, alias]))

/** Turn bare portal paths and menu shortcuts into markdown links. */
export function linkifyPortalPaths(content: string, routes?: AssistantRouteTable | null): string {
  const match = (full: string, path: string) => linkifyMatch(content, full, path, routes)
  const withPaths = content.replace(PORTAL_PATH_RE, match)
  return withPaths.replace(shortcutPattern(routes), match)
}

/** Renders assistant replies as markdown with clickable portal links. */
export const AssistantMarkdown: React.FC<AssistantMarkdownProps> = ({
  content,
  onNavigate,
  routes = null,
  dense = false,
}) => {
  const linked = useMemo(() => linkifyPortalPaths(content, routes), [content, routes])
  if (!linked) return null

  const textVariant = dense ? 'caption' : 'body2'
  const bodyFontSize = dense ? '0.8125rem' : undefined
  const bodyLineHeight = dense ? 1.4 : undefined

  return (
    <Box
      sx={{
        fontSize: bodyFontSize,
        lineHeight: bodyLineHeight,
        '& p': { m: 0, mb: dense ? 0.75 : 1, '&:last-child': { mb: 0 } },
        '& ul, & ol': { m: 0, mb: dense ? 0.75 : 1, pl: dense ? 2 : 2.5 },
        '& li': { mb: 0.25 },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: dense ? '0.8em' : '0.85em',
          px: 0.5,
          py: 0.15,
          borderRadius: 0.5,
          bgcolor: 'action.selected',
        },
        '& pre': {
          m: 0,
          mb: dense ? 0.75 : 1,
          p: dense ? 0.75 : 1,
          borderRadius: 1,
          overflow: 'auto',
          bgcolor: 'action.hover',
          '& code': { p: 0, bgcolor: 'transparent' },
        },
        '& h1, & h2, & h3, & h4': {
          m: 0,
          mb: 0.5,
          mt: 0.35,
          fontSize: dense ? '0.875rem' : '0.95rem',
          fontWeight: 600,
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          mb: dense ? 0.75 : 1,
          fontSize: dense ? '0.72rem' : '0.8rem',
        },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          px: dense ? 0.5 : 0.75,
          py: dense ? 0.25 : 0.35,
          textAlign: 'left',
        },
        '& a': {
          fontWeight: 600,
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Typography
              variant={textVariant}
              component='p'
              sx={{ fontSize: bodyFontSize, lineHeight: bodyLineHeight }}
            >
              {children}
            </Typography>
          ),
          a: ({ href, children }) => {
            const portalPath = href ? resolveAssistantPath(href, routes) : null
            if (portalPath && onNavigate) {
              return (
                <Link
                  component='button'
                  type='button'
                  variant={textVariant}
                  onClick={event => {
                    event.preventDefault()
                    onNavigate(portalPath)
                  }}
                  sx={{
                    verticalAlign: 'baseline',
                    border: 0,
                    background: 'none',
                    p: 0,
                    font: 'inherit',
                    fontSize: bodyFontSize,
                    cursor: 'pointer',
                  }}
                >
                  {children}
                </Link>
              )
            }
            if (
              href &&
              (href.startsWith('/') || href.includes('optictruckworks.com'))
            ) {
              return (
                <Typography
                  component='span'
                  variant={textVariant}
                  sx={{ fontSize: bodyFontSize }}
                >
                  {children}
                </Typography>
              )
            }
            return (
              <Link
                href={href}
                target='_blank'
                rel='noopener noreferrer'
                variant={textVariant}
                sx={{ fontSize: bodyFontSize }}
              >
                {children}
              </Link>
            )
          },
          li: ({ children }) => (
            <Typography
              variant={textVariant}
              component='li'
              sx={{ fontSize: bodyFontSize, lineHeight: bodyLineHeight }}
            >
              {children}
            </Typography>
          ),
        }}
      >
        {linked}
      </ReactMarkdown>
    </Box>
  )
}
